import _ from 'lodash';
import { FILTER_COMPONENT_TYPES, convertStringifiedNullToNull } from 'helpers/sf_constants';
import { mapActiveFilters } from 'helpers/conversation_filter_helper';
import { sanitizeProspectFilters } from 'helpers/sf_utils';

/**
 * Closes the filter modal by toggling its visibility and active state.
 * 
 * @param {Function} setIsActiveFilterModal - Function to set the active state of the filter modal.
 */
export const onCloseFilterModal = (setIsActiveFilterModal) => {
    setIsActiveFilterModal(false);
}
/**
 * Removes a filter from the active filters list and fetches the next batch of data.
 * 
 * @param {string} field_name - Name of the filter field to be removed.
 * @param {Function} setFilterItems - Setstate to update the prospect filter items state.
 * @param {Function} fetchNextBatchWrapper - Function to fetch the next batch of data.
 * @param {Object|null} searchInputFilter - Optional search input filter to be included in the active filters.
 *  * @param {Object} special_cases - An object containing special cases for filter sanitization or mapping. Used to handle edge cases in filtering logic.
 */
export const onClickRemoveFilter = (field_name, setFilterItems, fetchNextBatchWrapper, special_cases) => {
    setFilterItems((prev_prospect_filter_items) => {
        const cloned_prev_items = _.cloneDeep(prev_prospect_filter_items)
        const mapped_filter_items = cloned_prev_items.map((item) => {
            if (item.name !== field_name) {
                return item;
            }
            // For COMPARATOR type filters, also clear the comparator_value (number input)
            if (item.component_type === FILTER_COMPONENT_TYPES.COMPARATOR) {
                return ({ ...item, is_active: false, value: null, comparator_value: "" })
            }
            return ({ ...item, is_active: false, value: null })
        });
        const filtered_filter_items = (mapped_filter_items || []).filter(item => item.is_active);

        fetchNextBatchWrapper(mapActiveFilters(filtered_filter_items, special_cases))
        return mapped_filter_items;
    });
}

/**
 * Opens the filter modal and updates the prospect filter items state.
 * 
 * @param {Function} setIsActiveFilterModal - Function to set the active state of the filter modal.
 * @param {Function} setFilterItems - Function to update the prospect filter items state.
 * @param {Object} mapped_filter_items - Object containing mapped filter items.
 */
export const onClickOpenFilterModal = (setIsActiveFilterModal, setFilterItems, mapped_filter_items) => {
    setIsActiveFilterModal(true);
    const mapped_filter_items_arr_objects = Object.values(mapped_filter_items);
    setFilterItems((prev_prospect_filter_items) => {
        const cloned_prev_items = _.cloneDeep(prev_prospect_filter_items)
        const new_mapped_filter_items = mapped_filter_items_arr_objects.map((item) => {
            const active_filters = cloned_prev_items?.find(prev_item => prev_item.name === item.name);
            if (active_filters) {
                return active_filters;
            }
            return item;
        });
        return new_mapped_filter_items;
    });
}

/**
 * Fetches the next batch of data based on filters and pagination.
 * 
 * @param {Object|null} filterModal - Object containing filter data. Defaults to null.
 * @param {number} last_page_fetched - The index of the last page fetched. Defaults to 0.
 * @param {boolean} loading - Indicates whether data is currently being loaded. Defaults to false.
 * @param {Function} dispatch - Redux dispatch function to trigger actions.
 * @param {Function} action - Redux action to fetch data.
 */
export const fetchNextBatch = (filterModal = null, last_page_fetched = 0, loading = false, dispatch, action) => {
    try {
        const queryParams = {}
        if (filterModal && Object.keys(filterModal)?.length) {
            const filterModalData = convertStringifiedNullToNull(filterModal)
            queryParams.filters = JSON.stringify(filterModalData || {})
        }
        queryParams.page_number = last_page_fetched + 1

        if (!loading) {
            dispatch(action({ queryParams: queryParams }, true))
        }
    } catch (error) {
        console.error("Error in fetchNextBatch:", error);
    }
};

/**
 * Handles the "Load More Data" functionality by processing filters and fetching the next batch of data.
 * 
 * @param {Array|null} prospect_filter_items - The list of filter items applied to prospects. Defaults to null if no filters are provided.
 * @param {number|null} filterCount - The count of filters applied. Determines whether to fetch filtered data or not. Defaults to null.
 * @param {number|null} last_page_fetched - The index of the last page fetched. Used for pagination. Defaults to null.
 * @param {Function} fetchNextBatchWrapper - A callback function to fetch the next batch of data. Takes filtered data and the last page fetched as arguments.
 * @param {Object} special_cases - An object containing special cases for filter sanitization or mapping. Used to handle edge cases in filtering logic.
 */
export const onClickLoadMoreData = (prospect_filter_items = null, filterCount = null, last_page_fetched = null, fetchNextBatchWrapper, special_cases) => {
    const active_filtered_columns = { data: null }
    if (prospect_filter_items) {
        active_filtered_columns.data = mapActiveFilters(sanitizeProspectFilters(prospect_filter_items), special_cases)
    }
    fetchNextBatchWrapper(filterCount ? active_filtered_columns.data : null, last_page_fetched)
}

/**
 * Retrieves active prospect filters and their count.
 * 
 * @param {Array} prospectFilterItems - Array of prospect filter items.
 * @returns {Object} - An object containing the count of active filters and the active filter items.
 */
export const getActiveProspectFilters = (prospectFilterItems) => {
    if (!prospectFilterItems?.length) return { filterCount: 0, activeProspectFilterItems: [] };

    const activeProspects = prospectFilterItems.filter(item => {
        const valueField = item?.component_type === FILTER_COMPONENT_TYPES.COMPARATOR ? item?.comparator_value : item?.value;
        return item.is_active && (valueField?.length || valueField);
    });

    return { filterCount: activeProspects.length, activeProspectFilterItems: activeProspects };
};

/**
 * Handles opening the filter modal and processing filter values.
 * 
 * @param {Array} values - Filter values submitted by the user.
 * @param {Function} setIsActiveFilterModal - Function to set the active state of the filter modal.
 * @param {Function} setFilterItems - Function to update the prospect filter items state.
 * @param {Function} fetchNextBatchWrapper - Wrapper function to fetch the next batch of data.
 * @param {Object} special_cases - An object containing special cases for filter sanitization or mapping.
 */
export const onSubmitOpenFilterModal = (values, setIsActiveFilterModal, setFilterItems, fetchNextBatchWrapper, special_cases) => {
    setIsActiveFilterModal(true);
    const activeFilteredColumns = sanitizeProspectFilters(values);
    setFilterItems(activeFilteredColumns);
    if (activeFilteredColumns?.length) {
        onClickLoadMoreData(activeFilteredColumns, true, null, fetchNextBatchWrapper, special_cases);
        onCloseFilterModal(setIsActiveFilterModal);
        return;
    }
    fetchNextBatchWrapper();
    onCloseFilterModal(setIsActiveFilterModal);
};
